import { Link } from "react-router-dom";
import { toast } from "sonner";
import ProductImage from "@/components/ProductImage";
import HeartButton from "@/components/HeartButton";
import OfferBadge, { Price } from "@/components/OfferBadge";
import { useCart } from "@/contexts/CartContext";
import { useLocation, openLocationPicker } from "@/contexts/LocationContext";

/** The size range of a product family ("1 kg – 10 kg") or its one size. */
export function sizeText(p) {
  if (p.sizes?.length > 1) return `${p.sizes[0].label} – ${p.sizes[p.sizes.length - 1].label}`;
  return p.size_variant || p.unit || "";
}

/** One product in a grid or a row: picture, brand and size, name, price and
 *  Add. Add puts the cheapest nearby shop's offer in the cart (guests too);
 *  products with several sizes open their page to pick one. */
export default function ProductTile({ p, testid }) {
  const { addItem } = useCart();
  const { looking } = useLocation();
  const o = p.best_offer;
  const quick = o && p.num_in_stock > 0 && !(p.size_count > 1);
  const add = (e) => {
    e.preventDefault();
    if (looking) { toast("Choose where to deliver first, so we can show the shops near you."); openLocationPicker(); return; }
    addItem({ id: `${p.id}::${o.id}`, offer_id: o.id, catalog_product_id: p.id, vendor_id: o.vendor_id, vendor_name: o.vendor_name,
      name: p.name, price: o.price, image_url: o.image_url || p.image_url }, 1);
    toast.success(`${p.name} added from ${o.vendor_name || "a shop near you"}`);
  };
  return (
    <Link to={`/product/${p.id}`} data-testid={testid || `product-${p.id}`}
      className="group flex flex-col h-full rounded-2xl overflow-hidden border border-stone-200 bg-white hover:shadow-lg transition-shadow">
      <div className="relative aspect-square bg-stone-100 overflow-hidden">
        <ProductImage src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <HeartButton p={p} className="absolute top-2 right-2" />
        <OfferBadge deal={p.deal} />
      </div>
      <div className="p-3 flex flex-col flex-1">
        <div className="text-[11px] text-stone-500 truncate">{[p.brand, sizeText(p)].filter(Boolean).join(" · ")}</div>
        <div className="text-sm font-semibold mt-0.5 line-clamp-2 leading-snug flex-1">{p.name}</div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="min-w-0">
            {p.size_count > 1 && <span className="text-[10px] text-stone-500 mr-1">from</span>}
            <Price price={p.min_price ?? p.price} mrp={p.size_count > 1 ? null : o?.mrp} className="font-bold" />
          </div>
          {quick ? (
            <button type="button" onClick={add} data-testid={`add-${p.id}`}
              className="shrink-0 rounded-full bg-marigold text-stoneWarm px-3.5 py-1.5 text-xs font-bold hover:bg-marigold/90">Add</button>
          ) : p.size_count > 1 ? (
            <span className="shrink-0 rounded-full border border-stone-300 px-2.5 py-1 text-[11px] font-semibold text-stone-600">{p.size_count} sizes</span>
          ) : !o ? <span className="shrink-0 text-[11px] text-stone-500">Out of stock</span> : null}
        </div>
      </div>
    </Link>
  );
}
